import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator
} from 'firebase/functions';
import { auth, appCheck, firebaseConfig } from '../../firebase';
import { toProperCase } from '../../utils/TextProcessing';
import { useDispatch } from 'react-redux';
import useCreate from '../create';
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  signInWithEmailAndPassword,
  sendSignInLinkToEmail,
  sendEmailVerification,
  signInWithPhoneNumber
} from 'firebase/auth';
import { keyUpdatedAction } from '../../redux/actions/local';
import { userLogoutAction } from '../../redux/actions/user';
import { logDocumentEvent } from '../actions/steps/utils';
import { getFirestore } from 'firebase/firestore';
import { getToken } from 'firebase/app-check';
import { loadUser } from '../../hooks/authenticated';

const useUser = (props = { ignoreAutomations: true }) => {
  const { createDocument: createDoc } = useCreate(props?.ignoreAutomations);

  const dispatch = useDispatch();
  const functions = getFunctions();
  if (process.env.NODE_ENV === 'development') {
    connectFunctionsEmulator(functions, 'localhost', 5001);
  }

  const chars =
    '0123456789abcdefghijklmnopqrstuvwxyz!@#$%^&*()ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  const login = async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password)
      .then(async userCredential => {
        const user = userCredential.user;

        await user.getIdToken(true);
        const decodedToken = await auth.currentUser.getIdTokenResult();

        return { ...userCredential, claims: decodedToken?.claims || {} };
      })
      .catch(error => {
        return {
          error,
          code: error.code
        };
      });
  };

  const accountExistsWithEmail = async email => {
    let result;
    let data;
    try {
      const appCheckTokenResponse = await getToken(
        appCheck
        /* forceRefresh= false*/
      );

      result = await fetch(
        `https://us-central1-${firebaseConfig.projectId}.cloudfunctions.net/user_account_exists`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Firebase-AppCheck': appCheckTokenResponse.token
          },
          body: JSON.stringify({
            data: {
              email
            }
          }),
          mode: 'cors'
        }
      );

      data = await result.json();
    } catch (error) {
      return { error };
    }

    return data.exists;
  };

  const accountExistsWithPhoneNumber = async mobile => {
    const accountExistsFunction = httpsCallable(
      functions,
      'user_account_exists',
      {
        limitedUseAppCheckTokens: true
      }
    );
    let result;
    try {
      result = await accountExistsFunction({
        mobile
      });
    } catch (error) {
      return { error };
    }

    return result?.data ? result.data : false;
  };

  /*
    change a users role via the backoffice
  */
  const adminChangeUserRole = async payload => {
    const changeRoleFunction = httpsCallable(functions, 'user_change_role', {
      limitedUseAppCheckTokens: true
    });
    const result = await changeRoleFunction(payload);
    return result?.data ? result.data : false;
  };

  /*
    create a user via the backoffice so we create users with roles that are not restricted to { canRegister }
  */
  const adminCreateUser = async payload => {
    const userCreateFunction = httpsCallable(functions, 'user_create', {
      limitedUseAppCheckTokens: true
    });
    const result = await userCreateFunction(payload);
    return result?.data ? result.data : false;
  };

  /* creates a user in the users collection in firestore */
  const registerAndCreateUser = async ({
    firstName,
    lastName,
    email = '',
    password,
    phone = '',
    role = '',
    photoUrl,
    signupMethod = 'email'
  }) => {
    let userPassword = password;
    if (!userPassword) {
      for (var i = 0; i <= 8; i++) {
        var randomNumber = Math.floor(Math.random() * chars.length);
        userPassword += chars.substring(randomNumber, randomNumber + 1);
      }
    }

    const db = getFirestore();

    return createUserWithEmailAndPassword(auth, email, userPassword)
      .then(async userCredential => {
        // Signed up
        const user = userCredential.user;

        const newUser = {};
        newUser.firstName = toProperCase(firstName);
        newUser.lastName = toProperCase(lastName);
        newUser.email = email;
        newUser.mobile = phone;
        newUser.signupMethod = signupMethod;
        newUser.userID = user.uid;
        newUser.role = role;
        newUser.deleted = false;
        newUser.lastUpdatedBy = email;
        if (photoUrl) {
          newUser.profilePhoto = [
            {
              original: photoUrl,
              thumb: photoUrl
            }
          ];
        }

        const { document: registeredUser } = await createDoc({
          data: newUser,
          contentTypeName: 'user',
          path: 'users'
        });

        // We load the user after we have created their firestore document
        await loadUser({
          source: 'registerAndCreateUser',
          user,
          registrationInProgress: false,
          dispatch
        });

        await logDocumentEvent({
          db,
          databaseId: '(default)',
          contentTypeName: 'user',
          documentAction: 'onDocumentCreated',
          documentId: user.uid,
          path: `users/${user.uid}`,
          after: registeredUser,
          source: `hooks.useUser.registerAndCreateUser`
        });

        return registeredUser;
      })
      .catch(error => {
        dispatch(keyUpdatedAction('registerAndCreateUser', { error }));
        return {
          code: error.code,
          error
        };
      });
  };

  const emailIsUnique = async email => {
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
      return false;
    }
    const emailIsUniqueFunction = httpsCallable(
      functions,
      'user_email_unique',
      {
        limitedUseAppCheckTokens: true
      }
    );
    const result = await emailIsUniqueFunction({
      email
    });
    return result?.data ? result.data : false;
  };

  const changeEmail = async email => {
    const changeEmailFunction = httpsCallable(functions, 'user_email_change', {
      limitedUseAppCheckTokens: true
    });
    const result = await changeEmailFunction({
      email
    });

    return result;
  };

  const sendPasswordResetLink = async email => {
    return sendPasswordResetEmail(auth, email)
      .then(() => {
        // Password reset email sent!
        return {
          success: true
        };
      })
      .catch(error => {
        return {
          error: `Error: ${error.code}: ${error.message}`
        };
      });
  };

  const sendEmailVerificationLink = async () => {
    return sendEmailVerification(auth.currentUser())
      .then(result => {
        // Password reset email sent!
        return {
          success: true,
          result
        };
      })
      .catch(error => {
        return {
          error: `Error: ${error.code}: ${error.message}`
        };
      });
  };

  const sendPhoneVerificationCode = async (phoneNumber, appVerifier) => {
    return signInWithPhoneNumber(auth, phoneNumber, appVerifier)
      .then(confirmationResult => {
        // SMS sent. Prompt user to type the code from the message, then sign the
        // user in with confirmationResult.confirm(code).
        return {
          confirmationResult
        };
        // ...
      })
      .catch(error => {
        // Error; SMS not sent
        return {
          error: true,
          message: `Error: ${error.code}: ${error.message}`,
          code: error.code
        };
      });
  };

  const logout = async () => {
    dispatch(userLogoutAction());
    return signOut(auth)
      .then(() => {
        // Sign-out successful.
        return {
          success: true
        };
      })
      .catch(error => {
        return {
          error: `Error: ${error.code}: ${error.message}`,
          code: error.code
        };
      });
  };

  const sendLoginLink = async (email, redirectBackTo) => {
    const actionCodeSettings = {
      // URL you want to redirect back to. The domain (www.example.com) for this
      // URL must be in the authorized domains list in the Firebase Console.
      url: redirectBackTo,
      // This must be true.
      handleCodeInApp: true
      //dynamicLinkDomain: 'example.page.link'
    };

    return sendSignInLinkToEmail(auth, email, actionCodeSettings)
      .then(() => {
        // The link was successfully sent. Inform the user.
        // Save the email locally so you don't need to ask the user for it again
        // if they open the link on the same device.
        window.localStorage.setItem('emailForSignIn', email);
        dispatch(
          keyUpdatedAction('passwordLessLogin', {
            loginLinkSent: true,
            email,
            actionCodeSettings
          })
        );
        return {
          success: true,
          email
        };
        // ...
      })
      .catch(error => {
        return {
          error
        };
      });
  };

  return {
    registerAndCreateUser,
    emailIsUnique,
    accountExistsWithEmail,
    accountExistsWithPhoneNumber,
    changeEmail,
    adminCreateUser,
    adminChangeUserRole,
    sendPasswordResetLink,
    sendEmailVerificationLink,
    sendPhoneVerificationCode,
    sendLoginLink,
    logout,
    login
  };
};

export default useUser;
